// domToMarkdown.ts - Refactored to TypeScript with types, error handling, and modularization

/**
 * Supported HTML tags and attributes for markdown conversion
 */
export const MEANINGFUL_TAGS = ['a', 'button', 'img', 'input', 'textarea', 'select', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'ul', 'ol', 'strong', 'em', 'b', 'i'];
export const MEANINGFUL_ATTRIBUTES = ['href', 'src', 'alt', 'value', 'type', 'placeholder', 'title', 'aria-label'];
/**
 * Cleans a DOM node, extracting only meaningful structure and attributes.
 * @param node DOM Node to clean
 * @returns CleanNode or null if node should be skipped
 */
export function cleanNode(node) {
  try {
    if (node.nodeType === Node.TEXT_NODE) {
      var _node$textContent;
      const text = ((_node$textContent = node.textContent) == null ? void 0 : _node$textContent.trim()) || '';
      return text ? {
        type: 'text',
        text
      } : null;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const el = node;
    const tag = el.tagName.toLowerCase();
    if (['script', 'style', 'meta', 'link', 'noscript'].includes(tag)) {
      return null;
    }
    const children = Array.from(el.childNodes).map(child => {
      try {
        return cleanNode(child);
      } catch (err) {
        // Fallback: skip problematic child
        return null;
      }
    }).filter(Boolean);
    const isContentTag = MEANINGFUL_TAGS.includes(tag);
    const attributes = {};
    if (el.attributes) {
      for (const attr of Array.from(el.attributes)) {
        if (MEANINGFUL_ATTRIBUTES.includes(attr.name)) {
          attributes[attr.name] = attr.value;
        }
      }
    }
    if (isContentTag || Object.keys(attributes).length || tag === 'body') {
      const obj = {
        type: 'element',
        tag
      };
      if (Object.keys(attributes).length) {
        obj.attributes = attributes;
      }
      if (children.length) {
        obj.children = children;
      }
      return obj;
    }
    if (children.length === 1) {
      return children[0];
    }
    if (children.length > 1) {
      return {
        type: 'group',
        children
      };
    }
    return null;
  } catch (error) {
    // Fallback: skip problematic node
    return null;
  }
}

/**
 * Converts a cleaned node structure to Markdown.
 * @param node CleanNode to convert
 * @param depth Recursion depth (for nested lists, etc.)
 * @returns Markdown string
 */
export function domToMarkdown(node, depth) {
  var _node$attributes, _node$attributes2, _node$attributes3, _node$attributes7, _node$attributes8;
  if (depth === void 0) {
    depth = 0;
  }
  try {
    if (!node) {
      return JSON.stringify({
        error: 'No node provided'
      });
    }
    if (node.type === 'text') {
      return node.text;
    }
    if (node.type === 'element') {
      const tag = node.tag.toLowerCase();
      const children = (node.children || []).map(child => {
        try {
          return domToMarkdown(child, depth);
        } catch (err) {
          return JSON.stringify({
            error: 'Failed to convert child node'
          });
        }
      }).filter(Boolean);
      const text = children.join('').trim();
      switch (tag) {
        case 'h1':
          return `# ${text}\n\n`;
        case 'h2':
          return `## ${text}\n\n`;
        case 'h3':
          return `### ${text}\n\n`;
        case 'h4':
          return `#### ${text}\n\n`;
        case 'h5':
          return `##### ${text}\n\n`;
        case 'h6':
          return `###### ${text}\n\n`;
        case 'p':
          return `${text}\n\n`;
        case 'strong':
        case 'b':
          return `**${text}**`;
        case 'em':
        case 'i':
          return `*${text}*`;
        case 'a':
          return `[${text}](${((_node$attributes = node.attributes) == null ? void 0 : _node$attributes.href) || '#'})`;
        case 'img':
          return `![${((_node$attributes2 = node.attributes) == null ? void 0 : _node$attributes2.alt) || ''}](${((_node$attributes3 = node.attributes) == null ? void 0 : _node$attributes3.src) || ''})`;
        case 'ul':
          return children.map(c => `- ${c}`).join('\n') + '\n\n';
        case 'ol':
          return children.map((c, i) => `${i + 1}. ${c}`).join('\n') + '\n\n';
        case 'li':
          return `${text}`;
        case 'input':
          {
            var _node$attributes4;
            const type = (_node$attributes4 = node.attributes) == null ? void 0 : _node$attributes4.type;
            if (type === 'checkbox' || type === 'radio') {
              var _node$attributes5;
              return `- [ ] ${((_node$attributes5 = node.attributes) == null ? void 0 : _node$attributes5['aria-label']) || 'option'}`;
            }
            if (type === 'text') {
              var _node$attributes6;
              return `> [Input field: ${((_node$attributes6 = node.attributes) == null ? void 0 : _node$attributes6.placeholder) || 'text'}]`;
            }
            return `> [Input: ${type || 'field'}]`;
          }
        case 'select':
          return `> [Dropdown: ${((_node$attributes7 = node.attributes) == null ? void 0 : _node$attributes7['aria-label']) || 'select'}]`;
        case 'button':
          return `**[Button: ${((_node$attributes8 = node.attributes) == null ? void 0 : _node$attributes8['aria-label']) || text || 'button'}]**`;
        default:
          return children.join('');
      }
    }
    if (node.type === 'group') {
      return (node.children || []).map(child => domToMarkdown(child, depth)).join('');
    }
    return '';
  } catch (error) {
    // Fallback: return empty string on error
    return JSON.stringify(error);
  }
}

// Utility to run on a DOM (optional, not exported)
export function documentToMarkdown() {
  try {
    const minimalDOM = cleanNode(document.body);
    return domToMarkdown(minimalDOM);
  } catch (error) {
    return JSON.stringify(error);
  }
}

// Example usage (commented out for modularity)
// const markdown = extractMarkdownFromDOM(document.body);
// console.log('📄 Markdown Output:\n\n' + markdown);
//# sourceMappingURL=domToMarkdown.js.map